import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheck, FiChevronDown, FiArrowRight, FiShield, 
  FiActivity, FiZap, FiSearch, FiLayers, FiTool, FiClock, FiShare2,
  FiTarget, FiMonitor, FiSettings, FiBriefcase, FiTrendingUp
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const TechnologyConsulting = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Technology Consulting | ManagedSite.net";
    
    const description = "ManagedSite.net provides technology consulting, including technology planning, vendor evaluation, platform selection, hosting reviews, security planning and digital operations guidance.";
    const title = "Technology Consulting | ManagedSite.net";
    
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const whyMatters = [
    {
      title: "Too Many Vendors",
      text: "Different providers handle hosting, email, development and support with little coordination.",
      icon: FiLayers
    },
    {
      title: "Unclear Priorities",
      text: "Teams spend money on tools and fixes without a practical roadmap.",
      icon: FiTarget
    },
    {
      title: "Risk Blind Spots",
      text: "Security, backups, DNS, deliverability and access control get overlooked until something breaks.",
      icon: FiShield
    },
    {
      title: "Growth Pressure",
      text: "Agencies need stronger technical systems as client volume and complexity increase.",
      icon: FiTrendingUp
    }
  ];

  const consultingCovers = [
    {
      title: "Technology Planning",
      description: "Clarify what systems you need, what can wait and how the pieces should work together.",
      examples: ["Current-state review", "Priority roadmap", "Tool stack simplification", "Operating recommendations", "Short-term and longer-term planning"],
      icon: FiSettings
    },
    {
      title: "Vendor Evaluation",
      description: "Compare providers and tools with a practical eye toward cost, risk, support and fit.",
      examples: ["Hosting vendor review", "SaaS and plugin evaluation", "Support provider assessment", "Overlap and waste identification", "Recommendation summary"],
      icon: FiSearch
    },
    {
      title: "Platform Selection",
      description: "Help choose platforms that fit the business model, team capacity and maintenance reality.",
      examples: ["CMS recommendations", "Ecommerce platform guidance", "Membership platform review", "Migration feasibility", "Build vs buy decisions"],
      icon: FiMonitor
    },
    {
      title: "Hosting and Infrastructure Review",
      description: "Review hosting, DNS, environments and operational readiness.",
      examples: ["Hosting fit assessment", "Staging and production review", "DNS and SSL review", "Backup and recovery gaps", "Performance risk review"],
      icon: FiLayers
    },
    {
      title: "Security and Incident Planning",
      description: "Strengthen practical security posture and response readiness.",
      examples: ["Access control review", "Security hardening priorities", "Backup recovery planning", "Incident response outline", "Risk reduction roadmap"],
      icon: FiShield
    },
    {
      title: "Digital Operations Guidance",
      description: "Improve the way technical work is requested, tracked, delivered and maintained.",
      examples: ["Support workflow design", "Maintenance process improvement", "Documentation standards", "Client handoff process", "Technical budgeting guidance"],
      icon: FiBriefcase
    }
  ];

  const faqs = pageFaqs['/services/technology-consulting'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] overflow-hidden pt-24 pb-24 lg:pt-40 lg:pb-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-20 items-center">
            <div className="lg:col-span-7 text-center lg:text-left mb-20 lg:mb-0">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Technology Consulting</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10">
                Strategic Technology Guidance Without a Full-Time Hire
              </h1>
              <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-2xl mx-auto lg:mx-0 font-medium">
                Better technology decisions across websites, hosting, email, security, vendors, and digital operations — without a full-time hire.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/digital-review" className="home-button">
                  Request a Consulting Review
                </Link>
                <Link to="/contact" className="home-button-secondary">
                  Tell Us What You Need
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] bg-[#EAF1EC] border border-[#3B7C6D]/20 shadow-2xl p-10 sm:p-12">
                <div className="absolute top-0 left-0 w-full h-2 bg-[#174C3C]"></div>
                <div className="space-y-6">
                  {[
                    { icon: FiSettings, label: "Technology Planning", status: "Clear" },
                    { icon: FiSearch, label: "Vendor Evaluation", status: "Reviewed" },
                    { icon: FiMonitor, label: "Platform Selection", status: "Guided" },
                    { icon: FiShield, label: "Security Planning", status: "Practical" },
                    { icon: FiBriefcase, label: "Digital Operations", status: "Organized" },
                    { icon: FiShare2, label: "Advisory Support", status: "Available" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl flex items-center justify-between border border-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#EAF1EC] p-3 rounded-xl text-[#174C3C]">
                          <SafeIcon icon={item.icon} className="w-6 h-6" />
                        </div>
                        <span className="font-extrabold text-[#17211E]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-3 py-1 rounded-full">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Consulting Matters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Technology Decisions Get Expensive When They Are Guessed</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Agencies and businesses often stitch together websites, hosting, email, plugins, SaaS tools and support vendors without a clear operating plan. Consulting creates clarity before problems multiply.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] hover:shadow-md transition-all">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Consulting Covers */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What Technology Consulting Includes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {consultingCovers.map((service, i) => (
              <div key={i} className="bg-white p-10 md:p-12 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-xl text-[#17211E]/75 mb-10 leading-relaxed font-medium">{service.description}</p>
                <div className="flex-grow">
                  <div className="text-sm font-bold text-[#3B7C6D] uppercase tracking-wider mb-6">Examples:</div>
                  <ul className="space-y-4">
                    {service.examples.map((item, j) => (
                      <li key={j} className="flex items-start text-[#17211E]/80 text-lg font-medium">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Consulting Built for Real Operating Needs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
            {[
              { title: "Agencies", text: "Agencies that need stronger technical systems, better vendor choices and clearer delivery processes." },
              { title: "Growing Service Businesses", text: "Organizations that have outgrown ad hoc technology decisions but do not need a full-time technical executive yet." },
              { title: "Project and Transition Moments", text: "Teams preparing for redesigns, migrations, hosting changes, security cleanup or major tooling decisions." }
            ].map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col text-center">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-8 flex-grow">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#17211E]/50 font-bold italic text-lg">
            Agencies and businesses can both start with a conversation about the setup.
          </p>
        </div>
      </section>

      {/* Ways to Work Together */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center">Ways to Work Together</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-20">
            {[
              { title: "One-Time Consulting Review", text: "A focused review of your current setup with prioritized recommendations." },
              { title: "Project Oversight", text: "Guidance during a migration, redesign, hosting change or technical cleanup project." },
              { title: "Ongoing Advisory Support", text: "Recurring guidance for agencies or organizations that need continued technical direction." }
            ].map((item, i) => (
              <div key={i} className="bg-white p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm flex flex-col">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-10 flex-grow">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="max-w-4xl mx-auto bg-white p-8 rounded-2xl mb-16">
            <p className="text-lg text-[#17211E]/70 font-medium leading-relaxed">
              Fractional CTO retainers are available as an ongoing advisory relationship. Request a consulting review to discuss fit.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button">
              Request a Consulting Review
            </Link>
            <Link to="/services/technical-support" className="home-button-secondary">
              View Technical Support
            </Link>
          </div>
        </div>
      </section>

      {/* How Consulting Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center leading-tight">How a Consulting Engagement Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { title: "Discovery", text: "We gather context on your systems, vendors, risks, goals and constraints." },
              { title: "Assessment", text: "We review the current environment and identify gaps, overlaps and priorities." },
              { title: "Recommendations", text: "We provide clear recommendations your team can act on, sequence and budget." },
              { title: "Implementation Support", text: "When needed, ManagedSite.net can help execute through technical support, website management, hosting or project work." }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Approach */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6">Consulting Is Scoped to the Decision</h2>
            <p className="text-xl text-[#17211E]/75 mb-10 font-medium leading-relaxed">
              Consulting engagements are quoted after a short discovery conversation. Smaller technical follow-up work can use agency support rates or prepaid blocks.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#17211E] mb-8 border-b border-[#EAF1EC] pb-6 uppercase tracking-wider">Advisory Engagements</h3>
              <div className="space-y-8">
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>Consulting retainers</span>
                  <span className="text-[#174C3C]">custom after review</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>Fractional CTO engagements</span>
                  <span className="text-[#174C3C]">custom after review</span>
                </div>
              </div>
              <p className="mt-12 text-lg text-[#17211E]/60 font-bold italic">
                We do not publish fixed consulting packages on this page because scope varies widely by environment and decision complexity.
              </p>
            </div>

            <div className="bg-[#EAF1EC] p-12 rounded-[2.5rem] border border-[#3B7C6D]/10 shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#17211E] mb-8 border-b border-[#3B7C6D]/10 pb-6 uppercase tracking-wider">Agency Support</h3>
              <div className="space-y-6 mb-10">
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>Agency technical support</span>
                  <span className="text-[#174C3C]">Partner rate</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>5-hour support block</span>
                  <span className="text-[#174C3C]">Prepaid</span>
                </div>
                <div className="flex justify-between items-center text-xl font-bold text-[#17211E]">
                  <span>10-hour support block</span>
                  <span className="text-[#174C3C]">Prepaid</span>
                </div>
              </div>
              <Link to="/contact" className="inline-flex items-center text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl gap-3">
                Discuss Your Scope <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* From Advice to Execution */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">From Advice to Execution</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              When recommendations need implementation, the same technical team can help.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Website Management", link: "/services/website-management" },
              { title: "Technical Support", link: "/services/technical-support" },
              { title: "Managed Hosting", link: "/services/managed-hosting" },
              { title: "Business Email", link: "/services/business-email" },
              { title: "Web Design and Development", link: "/services/web-design-and-development" },
              { title: "Agency Plans", link: "/agency-plans" }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-[#FAF8F3] p-10 rounded-[2rem] border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all text-xl">
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Agency Section */}
      <section className="py-32 bg-[#174C3C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Agencies</div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 leading-tight">A Technical Advisor Your Agency Can Rely On</h2>
              <p className="text-2xl text-[#FAF8F3]/80 mb-12 font-medium leading-relaxed">
                Get clearer technology direction for your agency operations and client delivery systems without hiring a full-time technical lead.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-16">
                {[
                  "Agency infrastructure guidance",
                  "Vendor and platform decisions",
                  "Support process improvement",
                  "Security and continuity planning",
                  "White-label friendly engagement"
                ].map((item, i) => (
                  <div key={i} className="flex items-start text-xl font-bold">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#D7B77A] mr-4 flex-shrink-0 mt-1" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="home-button-on-dark">
                  Request a Consulting Review
                </Link>
                <Link to="/agency-plans" className="home-button-on-dark-secondary">
                  View Agency Plans
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative select-none opacity-50 text-center">
              <div className="bg-white/5 rounded-[3rem] p-16 border border-white/10 backdrop-blur-sm">
                <div className="text-[#D7B77A] text-6xl font-black mb-10">ADVISE</div>
                <div className="h-1.5 w-24 bg-[#D7B77A] mx-auto mb-10"></div>
                <div className="space-y-6">
                  <div className="h-4 bg-white/20 rounded-full w-full"></div>
                  <div className="h-4 bg-white/20 rounded-full w-5/6 mx-auto"></div>
                  <div className="h-4 bg-white/20 rounded-full w-4/6 mx-auto"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Routing Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF8F3] rounded-[2.5rem] p-12 md:p-20 shadow-xl border border-[#EAF1EC] text-center max-w-5xl mx-auto">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Technology Consulting for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what you are trying to decide, and we will help work out the next step.
            </p>
            <Link to="/digital-review" className="home-button">
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-[#FAF8F3] border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-[#FAF8F3] transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`} 
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Get Clear Direction Before the Next Expensive Guess
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            Make better decisions about websites, hosting, email, security, vendors and digital operations with practical technical guidance.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button-on-dark">
              Request a Consulting Review
            </Link>
            <Link to="/agency-plans" className="home-button-on-dark-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnologyConsulting;